import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image
} from '@react-pdf/renderer';
import { DashboardDetalhadoColuna, DashboardDetalhadoSecao } from '../../../types/dashboard';

// Relatório genérico dos quadrantes do dashboard: uma tabela por card, seguindo o
// mesmo layout (paisagem, 6pt, cabeçalho cinza, linha de total) do relatório de
// faturamento (RelatorioAutorizacoesFaturamentoDocument.tsx).
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 20,
        paddingBottom: 30,
        fontFamily: 'Helvetica',
        fontSize: 7,
        color: '#333333',
    },
    headerBox: {
        flexDirection: 'row',
        marginBottom: 10,
        borderBottom: '0.5px solid #e0e0e0',
        paddingBottom: 8,
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    title: {
        fontSize: 14,
        fontFamily: 'Helvetica-Bold',
        color: '#333333',
    },
    subtitle: {
        fontSize: 9,
        color: '#666666',
        marginTop: 2,
    },
    note: {
        fontSize: 7,
        color: '#666666',
        marginTop: 3,
    },
    logo: {
        width: 100,
        height: 40,
        objectFit: 'contain',
    },
    section: {
        marginBottom: 14,
    },
    sectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: 2,
    },
    sectionTitle: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: '#2f3d52',
    },
    sectionCount: {
        fontSize: 8,
        color: '#2f3d52',
    },
    sectionDescription: {
        fontSize: 6.5,
        color: '#777777',
        marginBottom: 4,
    },
    tableContainer: {
        marginTop: 2,
        borderWidth: 0.5,
        borderColor: '#e0e0e0',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f5f5f5',
        borderBottomWidth: 0.5,
        borderBottomColor: '#e0e0e0',
        alignItems: 'center',
        minHeight: 15,
        paddingVertical: 2,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#e0e0e0',
        alignItems: 'center',
        minHeight: 12,
        paddingVertical: 2,
    },
    headerText: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 6,
    },
    cellText: {
        fontSize: 6,
    },
    emptyRow: {
        paddingVertical: 6,
        paddingLeft: 4,
    },
    totalRow: {
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: 16,
        paddingVertical: 3,
        backgroundColor: '#eef1f6',
        borderTopWidth: 0.8,
        borderTopColor: '#2f3d52',
    },
    totalText: {
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
    },
    footer: {
        position: 'absolute',
        bottom: 10,
        left: 20,
        right: 20,
        textAlign: 'center',
        fontSize: 6,
        color: '#999999',
        borderTopWidth: 0.5,
        borderTopColor: '#e0e0e0',
        paddingTop: 4,
    },
});

interface RelatorioIndicadoresDetalhadoDocumentProps {
    titulo: string;
    periodoLabel: string;
    observacao?: string;
    secoes: DashboardDetalhadoSecao[];
    logoUrl?: string;
}

const formatCurrency = (value: number) => {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const parts = value.substring(0, 10).split('-'); // YYYY-MM-DD
    if (parts.length !== 3) return value;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const formatCell = (coluna: DashboardDetalhadoColuna, value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return '-';
    switch (coluna.tipo) {
        case 'moeda':
            return formatCurrency(Number(value));
        case 'data':
            return formatDate(String(value));
        case 'inteiro':
        case 'quantidade':
            return String(value);
        default:
            return String(value);
    }
};

const isNumericColumn = (coluna: DashboardDetalhadoColuna) => coluna.tipo === 'moeda' || coluna.tipo === 'quantidade';

const buildWidths = (colunas: DashboardDetalhadoColuna[]): string[] => {
    const soma = colunas.reduce((acc, c) => acc + c.largura, 0) || 1;
    return colunas.map((c) => `${(c.largura / soma) * 100}%`);
};

const SecaoTabela: React.FC<{ secao: DashboardDetalhadoSecao }> = ({ secao }) => {
    const { colunas, itens, totais, resumo } = secao;
    const widths = buildWidths(colunas);

    const cellStyle = (index: number) => ({
        width: widths[index],
        paddingLeft: 4,
        paddingRight: 4,
        textAlign: (isNumericColumn(colunas[index]) ? 'right' : 'left') as 'right' | 'left',
    });

    // A linha de total abre com um rótulo que ocupa as colunas anteriores à
    // primeira coluna somada (mesmo desenho do relatório de faturamento).
    const primeiraColunaTotal = colunas.findIndex((c) => totais && totais[c.chave] !== undefined);
    const temTotal = primeiraColunaTotal >= 0;
    const colunasDoRotulo = primeiraColunaTotal > 0 ? primeiraColunaTotal : 0;
    const larguraRotulo = widths
        .slice(0, colunasDoRotulo)
        .reduce((acc, w) => acc + parseFloat(w), 0);

    const rotuloQuantidade = `${resumo.quantidade} ${resumo.quantidade === 1 ? 'registro' : 'registros'}`;
    const rotuloValor = resumo.valor_total !== null && resumo.valor_total !== undefined
        ? `  |  ${formatCurrency(resumo.valor_total)}`
        : '';

    return (
        <View style={styles.section}>
            <View>
                {/* minPresenceAhead evita título de seção órfão no fim da página */}
                <View style={styles.sectionHeader} minPresenceAhead={60}>
                    <Text style={styles.sectionTitle}>{secao.titulo}</Text>
                    <Text style={styles.sectionCount}>{rotuloQuantidade}{rotuloValor}</Text>
                </View>
                <Text style={styles.sectionDescription}>{secao.descricao}</Text>

                <View style={styles.tableContainer}>
                    <View style={styles.tableHeader}>
                        {colunas.map((coluna, index) => (
                            <View key={coluna.chave} style={cellStyle(index)}>
                                <Text style={styles.headerText}>{coluna.rotulo}</Text>
                            </View>
                        ))}
                    </View>

                    {itens.length === 0 && (
                        <View style={styles.emptyRow}>
                            <Text style={styles.cellText}>Nenhum registro encontrado neste indicador.</Text>
                        </View>
                    )}

                    {itens.map((item, rowIndex) => (
                        <View
                            key={rowIndex}
                            wrap={false}
                            style={[
                                styles.tableRow,
                                { backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f9f9f9' }
                            ]}
                        >
                            {colunas.map((coluna, index) => (
                                <View key={coluna.chave} style={cellStyle(index)}>
                                    <Text style={styles.cellText}>{formatCell(coluna, item[coluna.chave])}</Text>
                                </View>
                            ))}
                        </View>
                    ))}

                    {temTotal && (
                        <View style={styles.totalRow} wrap={false}>
                            {colunasDoRotulo > 0 ? (
                                <View style={{ width: `${larguraRotulo}%`, paddingLeft: 4 }}>
                                    <Text style={styles.totalText}>TOTAL ({resumo.quantidade})</Text>
                                </View>
                            ) : null}
                            {colunas.map((coluna, index) => {
                                if (index < colunasDoRotulo) return null;
                                const valor = totais[coluna.chave];
                                const mostraRotuloAqui = colunasDoRotulo === 0 && index === 0;
                                return (
                                    <View key={coluna.chave} style={cellStyle(index)}>
                                        <Text style={styles.totalText}>
                                            {valor !== undefined
                                                ? (coluna.tipo === 'moeda' ? formatCurrency(valor) : String(valor))
                                                : (mostraRotuloAqui ? `TOTAL (${resumo.quantidade})` : '')}
                                        </Text>
                                    </View>
                                );
                            })}
                        </View>
                    )}
                </View>
            </View>
        </View>
    );
};

export const RelatorioIndicadoresDetalhadoDocument: React.FC<RelatorioIndicadoresDetalhadoDocumentProps> = ({
    titulo,
    periodoLabel,
    observacao,
    secoes,
    logoUrl = '/assets/img/A2R_logo.png'
}) => {
    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>

                {/* Cabeçalho */}
                <View style={styles.headerBox}>
                    <View style={{ flexShrink: 1, paddingRight: 10 }}>
                        <Text style={styles.title}>{titulo}</Text>
                        <Text style={styles.subtitle}>Período: {periodoLabel}</Text>
                        <Text style={styles.subtitle}>Gerado em: {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}</Text>
                        {observacao ? <Text style={styles.note}>{observacao}</Text> : null}
                    </View>
                    <Image src={logoUrl} style={styles.logo} />
                </View>

                {/* Uma tabela por indicador */}
                {secoes.map((secao) => (
                    <SecaoTabela key={secao.chave} secao={secao} />
                ))}

                {/* Rodapé da Página */}
                <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                    `Página ${pageNumber} de ${totalPages}`
                )} fixed />
            </Page>
        </Document>
    );
};
